import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const linkReservationStatusSchema = z.enum([
  'reserved',
  'exchanged',
  'cancelled_before_success',
  'ambiguous',
]);

export const linkReservationSchema = z.object({
  reservation_id: z.string(),
  status: linkReservationStatusSchema,
  item_id: z.string().nullable(),
  job_id: z.string().nullable(),
  bootstrap_status: z.enum(['queued', 'running', 'complete', 'failed']).nullable(),
});

export type LinkReservation = z.infer<typeof linkReservationSchema>;

export function useCurrentLinkReservation() {
  return useQuery({
    queryKey: ['plaid', 'link-reservations', 'current'],
    queryFn: async (): Promise<LinkReservation | null> => {
      const response = await client.get('/api/plaid/link-reservations/current');
      return parseEnvelope(linkReservationSchema.nullable(), response.data).data;
    },
  });
}

export function useLinkReservation(reservationId: string | null | undefined) {
  return useQuery({
    queryKey: ['plaid', 'link-reservations', reservationId],
    enabled: Boolean(reservationId),
    queryFn: async (): Promise<LinkReservation> => {
      const response = await client.get(`/api/plaid/link-reservations/${reservationId}`);
      return parseEnvelope(linkReservationSchema, response.data).data;
    },
  });
}

export function useReportLinkExit() {
  return useMutation({
    mutationFn: async ({ reservationId, linkSessionId }: { reservationId: string; linkSessionId: string | null }) => {
      const response = await client.post(`/api/plaid/link-reservations/${reservationId}/cancel`, {
        link_session_id: linkSessionId,
      });
      return parseEnvelope(linkReservationSchema, response.data).data;
    },
  });
}
